/**
 * Week Settings Utility
 *
 * Provides locale-aware week start day detection with the following priority:
 * 1. User setting override (if explicitly set to 0 or 1)
 * 2. Intl.Locale.getWeekInfo() (if available in browser)
 * 3. Default to 1 (Monday - ISO standard)
 */

export type WeekStartDay = 0 | 1; // 0 = Sunday, 1 = Monday

/**
 * Detects the week start day from the browser's locale using Intl.Locale.getWeekInfo()
 * Returns 0 for Sunday, 1 for Monday
 * Falls back to 1 (Monday) if detection fails
 *
 * Note: Saturday-start locales (e.g., ar-SA, ar-AE) are mapped to Sunday (0) as a
 * graceful fallback since full Saturday support is not implemented. This provides
 * a better experience than defaulting to Monday for users in these regions.
 */
export function detectWeekStartFromLocale(): WeekStartDay {
  try {
    const locale = new Intl.Locale(navigator.language);
    // getWeekInfo() returns { firstDay: 1-7, weekend: number[], minimalDays: number }
    // where firstDay: 1 = Monday, ..., 6 = Saturday, 7 = Sunday
    if ('getWeekInfo' in locale && typeof locale.getWeekInfo === 'function') {
      const weekInfo = locale.getWeekInfo();
      // Convert from Intl format (1-7) to date-fns format (0-6, where 0=Sunday)
      // Saturday (6) and Sunday (7) both map to Sunday (0) as graceful fallback
      // Full Saturday support would require extensive changes to week numbering logic
      return weekInfo.firstDay >= 6 ? 0 : 1;
    }
  } catch {
    // Silently fall through to default
  }

  // Default to Monday (ISO standard)
  return 1;
}

/**
 * Gets the effective week start day based on user settings
 *
 * @param userSetting - The user's weekStartsOn setting (0, 1, 'auto', or null)
 * @returns 0 for Sunday, 1 for Monday
 */
export function getWeekStartDay(userSetting: 0 | 1 | 'auto' | null | undefined): WeekStartDay {
  // If user explicitly set 0 (Sunday) or 1 (Monday), use that
  if (userSetting === 0 || userSetting === 1) {
    return userSetting;
  }

  // For 'auto', null, or undefined, detect from browser locale
  return detectWeekStartFromLocale();
}

/**
 * Gets the week start day label for display
 */
export function getWeekStartDayLabel(day: WeekStartDay): string {
  return day === 0 ? 'Sunday' : 'Monday';
}

/**
 * Options for the week start day dropdown
 */
export const WEEK_START_OPTIONS = [
  { value: 'auto', label: 'Auto-detect from locale' },
  { value: 1, label: 'Monday (ISO standard)' },
  { value: 0, label: 'Sunday' },
] as const;
